import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Alert,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ProjectMetadata, Co2MaterialSettingType, ProjectResponse, FeatureManagementType } from "@api/apiClient";
import { ProjectMetadataForm } from "../../../dashboard/components/ProjectMetadataForm";
import RebarEstimationPage from "@features/settings/components/RebarEstimationPage/RebarEstimationPage";
import { BenchmarkTargetsForm } from "./BenchmarkTargetsForm";
import { useAdditionalMetadataFeature } from "@hooks/useAdditionalMetadataFeature";
import { useUpdateProjectMetadata } from "@api/projects";
import { useAppSettings } from "@api/appSettings";
import i18n from "@i18n/i18n";
import useAppStore from "@hooks/useAppStore";

interface ProjectSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  project: ProjectResponse | undefined;
  projectId: string;
}

export const ProjectSettingsDialog: React.FC<ProjectSettingsDialogProps> = ({
  open,
  onClose,
  project,
  projectId,
}) => {
  const { isEnabled: showMetadata } = useAdditionalMetadataFeature();
  const appSettings = useAppSettings();
  const appStore = useAppStore();
  const updateProjectMetadata = useUpdateProjectMetadata(projectId);
  const isBenchmarkEnabled = appStore.hasFeature(FeatureManagementType.BenchmarkCatalog);

  // State for metadata form
  const [metadata, setMetadata] = useState<ProjectMetadata>(
    new ProjectMetadata({
      totalFloorArea: undefined,
      serviceLife: undefined,
      goal: "",
      scope: ""
    })
  );

  // Track if metadata has changed
  const [hasMetadataChanged, setHasMetadataChanged] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Load metadata from project when project changes
  useEffect(() => {
    if (project?.project?.projectMetadata) {
      setMetadata(new ProjectMetadata({
        totalFloorArea: project.project.projectMetadata.totalFloorArea,
        serviceLife: project.project.projectMetadata.serviceLife,
        goal: project.project.projectMetadata.goal,
        scope: project.project.projectMetadata.scope,
      }));
    }
    setHasMetadataChanged(false);
  }, [project]);

  const handleMetadataChange = (name: string, value: any) => {
    setMetadata(prev => {
      const newData = {
        totalFloorArea: prev.totalFloorArea,
        serviceLife: prev.serviceLife,
        goal: prev.goal,
        scope: prev.scope,
        [name]: value
      };
      return new ProjectMetadata(newData);
    });
    setHasMetadataChanged(true);
  };

  const handleSaveMetadata = async () => {
    try {
      const projectMetadata = new ProjectMetadata({
        totalFloorArea: metadata.totalFloorArea,
        serviceLife: metadata.serviceLife,
        goal: metadata.goal,
        scope: metadata.scope,
      });

      await updateProjectMetadata.mutateAsync(projectMetadata);
      setHasMetadataChanged(false);
      setSuccessMessage(i18n.t("project-settings-page.metadata-saved"));
    } catch (error) {
      console.error("Error updating project metadata:", error);
      setErrorMessage(i18n.t("project-settings-page.metadata-save-error"));
    }
  };

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={"xl"}
        open={open}
        onClose={onClose}
        aria-labelledby="project-settings-dialog-title"
        PaperProps={{
          sx: {
            height: "80%",
          },
        }}
      >
        <DialogTitle
          id="project-settings-dialog-title"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {i18n.t("project-page.rebar-project-settings")}
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            display: "flex", 
            flexDirection: "column",
            height: "100%",
            paddingLeft: 2,
            paddingRight: 2,
            paddingBottom: 0,
          }}
        >
          {showMetadata && (
            <Box sx={{ mb: 2 }}>
              <ProjectMetadataForm
                metadata={metadata}
                onChange={handleMetadataChange}
              />
              {hasMetadataChanged && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveMetadata}
                    disabled={updateProjectMetadata.isPending}
                  >
                    {updateProjectMetadata.isPending
                      ? i18n.t("project-settings-page.save-metadata-button.on-saving")
                      : i18n.t("project-settings-page.save-metadata-button.title")
                    }
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {isBenchmarkEnabled && (
            <Box sx={{ mb: 2 }}>
              <BenchmarkTargetsForm
                projectId={projectId}
                project={project}
                onSaveSuccess={(msg: string) => setSuccessMessage(msg)}
                onSaveError={(msg: string) => setErrorMessage(msg)}
              />
            </Box>
          )}

          {(appSettings.data?.rebarEstimation || appSettings.data?.profileLevelRebarSettings) && project?.project?.profile && (
            <Box sx={{ flexGrow: 1, display: "flex" }}>
               <RebarEstimationPage 
                mode={appSettings.data?.profileLevelRebarSettings? Co2MaterialSettingType.Profile : Co2MaterialSettingType.Project} 
                projectId={projectId} 
                profileId={appStore.project?.profileId}
                originScreen='Project'
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          variant="filled"
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage("")}
          severity="error"
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
